import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createMeetingMinutes,
  fetchMeetingMinutesById,
  updateMeetingMinutes,
  fetchMeetingMinutesTemplates,
  fetchMeetingMinutes,
  continueMeetingMinutes,
  createMeetingMinutesGroup,
  createMeetingMinutesEntry,
} from "../../../api/meetingMinutes";
import { fetchUsers } from "../../../api/user";
import { fetchProjects } from "../../../api/project";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PMCustomSelector } from "../../../components/utils/PMCustomSelector";
import ModernProjectSelector from "../../../components/utils/Selector/ModernProjectSelector";
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  DocumentTextIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

export const MeetingMinutesEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const templateId = searchParams.get("template_id");
  const copyFromId = searchParams.get("copy_from");
  const continueMeeting = searchParams.get("continue") === "true";
  
  const [loading, setLoading] = useState(isEdit || !!templateId || !!copyFromId);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previousMeetings, setPreviousMeetings] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectObject, setSelectedProjectObject] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    meeting_type: "",
    employee_id: null,
    project_id: null,
    participants: [],
    manual_participants: "",
    manual_participants_list: [], // Array of {name, email, mobile}
  });

  const [meetings, setMeetings] = useState([
    {
      id: null,
      meeting_number: 1,
      meeting_date: "",
      groups: [
        {
          id: null,
          title: "",
          description: "",
          order: 0,
          entries: [
            {
              id: null,
              description: "",
              deadline: "",
              responsible_user_id: null,
              order: 0,
            },
          ],
        },
      ],
    },
  ]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // Load templates
        const templatesResponse = await fetchMeetingMinutesTemplates({ per_page: 100 });
        setTemplates(templatesResponse.data || []);
        
        // Load previous meetings for copy
        const meetingsResponse = await fetchMeetingMinutes({ per_page: 100 });
        setPreviousMeetings(meetingsResponse.data || []);
        
        // Load users for participant selection
        const usersResponse = await fetchUsers({ per_page: 100 });
        setUsers(usersResponse.data?.data || usersResponse.data || []);

        if (isEdit) {
          // Load existing meeting minutes
          const data = await fetchMeetingMinutesById(id);
          let manualParticipantsList = [];
          try {
            if (data.manual_participants) {
              manualParticipantsList = JSON.parse(data.manual_participants);
            }
          } catch (e) {
            // If not JSON, treat as old format string
            manualParticipantsList = [];
          }
          
          setFormData({
            title: data.title || "",
            description: data.description || "",
            meeting_type: data.meeting_type || "",
            employee_id: data.employee?.id || null,
            project_id: data.project?.id || null,
            participants: data.participants?.map((p) => p.id) || [],
            manual_participants: data.manual_participants || "",
            manual_participants_list: manualParticipantsList,
          });
          
          // Set selected project object if project exists
          if (data.project?.id) {
            setSelectedProjectObject(data.project);
            setSelectedProject(data.project.id);
          }
          
          if (data.meetings && data.meetings.length > 0) {
            setMeetings(data.meetings);
          } else {
            // Create initial meeting structure
            setMeetings([{
              id: null,
              meeting_number: 1,
              meeting_date: data.meeting_date || "",
              groups: [{
                id: null,
                title: "",
                description: "",
                order: 0,
                entries: [{
                  id: null,
                  description: "",
                  deadline: "",
                  responsible_user_id: null,
                  order: 0,
                }],
              }],
            }]);
          }
        } else if (templateId) {
          // Load template
          const templateResponse = await fetchMeetingMinutesTemplates({});
          const template = templateResponse.data?.find(t => t.id === parseInt(templateId));
          if (template) {
            setFormData({
              ...formData,
              meeting_type: template.meeting_type || "",
            });
            if (template.groups && template.groups.length > 0) {
              setMeetings([{
                id: null,
                meeting_number: 1,
                meeting_date: "",
                groups: template.groups.map((group, groupIdx) => ({
                  id: null,
                  title: group.title || "",
                  description: group.description || "",
                  order: group.order || groupIdx,
                  entries: (group.entries || []).map((entry, entryIdx) => ({
                    id: null,
                    description: entry.description || "",
                    deadline: entry.deadline || "",
                    responsible_user_id: null,
                    order: entry.order || entryIdx,
                  })),
                })),
              }]);
            }
          }
        } else if (copyFromId) {
          // Load previous meeting to copy from
          const copyData = await fetchMeetingMinutesById(copyFromId);
          setFormData({
            title: `${copyData.title} (Kopi)`,
            description: copyData.description || "",
            meeting_type: copyData.meeting_type || "",
            meeting_date: "",
            employee_id: copyData.employee?.id || null,
            project_id: copyData.project?.id || null,
            participants: copyData.participants?.map((p) => p.id) || [],
            manual_participants: copyData.manual_participants || "",
          });
          
          // Set selected project object if project exists
          if (copyData.project?.id) {
            setSelectedProjectObject(copyData.project);
            setSelectedProject(copyData.project.id);
          }
          
          if (copyData.meetings && copyData.meetings.length > 0) {
            const latestMeeting = copyData.meetings[copyData.meetings.length - 1];
            setMeetings([{
              id: null,
              meeting_number: 1,
              meeting_date: "",
              groups: (latestMeeting.groups || []).map((group, groupIdx) => ({
                id: null,
                title: group.title || "",
                description: group.description || "",
                order: group.order || groupIdx,
                entries: (group.entries || []).map((entry, entryIdx) => ({
                  id: null,
                  description: entry.description || "",
                  deadline: entry.deadline || "",
                  responsible_user_id: null,
                  order: entry.order || entryIdx,
                })),
              })),
            }]);
          }
        } else if (continueMeeting && id) {
          // Continue existing meeting (for project meetings)
          const existingData = await fetchMeetingMinutesById(id);
          setFormData({
            title: existingData.title,
            description: existingData.description || "",
            meeting_type: existingData.meeting_type || "",
            meeting_date: "",
            employee_id: existingData.employee?.id || null,
            project_id: existingData.project?.id || null,
            participants: existingData.participants?.map((p) => p.id) || [],
            manual_participants: existingData.manual_participants || "",
          });
          
          // Set selected project object if project exists
          if (existingData.project?.id) {
            setSelectedProjectObject(existingData.project);
            setSelectedProject(existingData.project.id);
          }
          
          const latestMeetingNumber = existingData.meetings?.length > 0 
            ? Math.max(...existingData.meetings.map(m => m.meeting_number || 1))
            : 0;
          
          setMeetings([{
            id: null,
            meeting_number: latestMeetingNumber + 1,
            meeting_date: "",
            groups: [{
              id: null,
              title: "",
              description: "",
              order: 0,
              entries: [{
                id: null,
                description: "",
                deadline: "",
                responsible_user_id: null,
                order: 0,
              }],
            }],
          }]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id, templateId, copyFromId, continueMeeting, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (continueMeeting && id) {
        // Continue meeting - create new meeting entry
        const meetingDate = meetings[0]?.meeting_date || formData.meeting_date || new Date().toISOString().split('T')[0];
        await continueMeetingMinutes(id, meetingDate);
        
        // Now create groups and entries for the new meeting
        // Get the updated meeting minutes to find the new meeting
        const updatedData = await fetchMeetingMinutesById(id);
        const newMeeting = updatedData.meetings?.find(m => m.meeting_number === meetings[0]?.meeting_number);
        
        if (newMeeting && meetings[0]?.groups) {
          for (const group of meetings[0].groups) {
            if (group.title) {
              const createdGroup = await createMeetingMinutesGroup(id, newMeeting.id, {
                title: group.title,
                description: group.description || "",
                order: group.order || 0,
              });
              
              if (group.entries) {
                for (const entry of group.entries) {
                  if (entry.description) {
                    await createMeetingMinutesEntry(id, newMeeting.id, createdGroup.id, {
                      description: entry.description,
                      deadline: entry.deadline || "",
                      responsible_user_id: entry.responsible_user_id || null,
                      order: entry.order || 0,
                    });
                  }
                }
              }
            }
          }
        }
        
        toast.success(t("meeting_minutes.updated"));
        navigate(`/dashboard/meeting-minutes/${id}`);
      } else if (isEdit) {
        // For edit, update the basic info
        const payload = {
          ...formData,
          manual_participants: formData.manual_participants_list?.length > 0 
            ? JSON.stringify(formData.manual_participants_list) 
            : formData.manual_participants,
        };
        await updateMeetingMinutes({ ...payload, id });
        toast.success(t("meeting_minutes.updated"));
        navigate("/dashboard/meeting-minutes");
      } else {
        // Create new meeting minutes - don't include meetings here, they're created separately
        const payload = {
          ...formData,
          manual_participants: formData.manual_participants_list?.length > 0 
            ? JSON.stringify(formData.manual_participants_list) 
            : formData.manual_participants,
        };
        
        const result = await createMeetingMinutes(payload, templateId);
        toast.success(t("meeting_minutes.created"));
        navigate("/dashboard/meeting-minutes");
      }
    } catch (err) {
      console.error("Error saving meeting minutes:", err);
      toast.error(isEdit ? t("meeting_minutes.update_error") : t("meeting_minutes.create_error"));
    } finally {
      setSaving(false);
    }
  };

  const addGroup = (meetingIndex) => {
    const newMeetings = [...meetings];
    const newGroup = {
      id: null,
      title: "",
      description: "",
      order: newMeetings[meetingIndex].groups.length,
      entries: [{
        id: null,
        description: "",
        deadline: "",
        responsible_user_id: null,
        order: 0,
      }],
    };
    newMeetings[meetingIndex].groups.push(newGroup);
    setMeetings(newMeetings);
  };

  const removeGroup = (meetingIndex, groupIndex) => {
    const newMeetings = [...meetings];
    newMeetings[meetingIndex].groups.splice(groupIndex, 1);
    setMeetings(newMeetings);
  };

  const updateGroup = (meetingIndex, groupIndex, field, value) => {
    const newMeetings = [...meetings];
    newMeetings[meetingIndex].groups[groupIndex][field] = value;
    setMeetings(newMeetings);
  };

  const addEntry = (meetingIndex, groupIndex) => {
    const newMeetings = [...meetings];
    const newEntry = {
      id: null,
      description: "",
      deadline: "",
      responsible_user_id: null,
      order: newMeetings[meetingIndex].groups[groupIndex].entries.length,
    };
    newMeetings[meetingIndex].groups[groupIndex].entries.push(newEntry);
    setMeetings(newMeetings);
  };

  const removeEntry = (meetingIndex, groupIndex, entryIndex) => {
    const newMeetings = [...meetings];
    newMeetings[meetingIndex].groups[groupIndex].entries.splice(entryIndex, 1);
    setMeetings(newMeetings);
  };

  const updateEntry = (meetingIndex, groupIndex, entryIndex, field, value) => {
    const newMeetings = [...meetings];
    newMeetings[meetingIndex].groups[groupIndex].entries[entryIndex][field] = value;
    setMeetings(newMeetings);
  };

  const handleUseTemplate = (template) => {
    setFormData({
      ...formData,
      meeting_type: template.meeting_type || "",
    });
    if (template.groups && template.groups.length > 0) {
      setMeetings([{
        id: null,
        meeting_number: 1,
        meeting_date: formData.meeting_date || "",
        groups: template.groups.map((group, groupIdx) => ({
          id: null,
          title: group.title || "",
          description: group.description || "",
          order: group.order || groupIdx,
          entries: (group.entries || []).map((entry, entryIdx) => ({
            id: null,
            description: entry.description || "",
            deadline: entry.deadline || "",
            responsible_user_id: null,
            order: entry.order || entryIdx,
          })),
        })),
      }]);
    }
    setShowTemplateDialog(false);
  };

  const handleCopyFrom = (previousMeeting) => {
    navigate(`/dashboard/meeting-minutes/new?copy_from=${previousMeeting.id}`);
  };

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={isEdit ? t("meeting_minutes.edit") : t("meeting_minutes.new")}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6">
          {/* Action buttons at top */}
          {!isEdit && (
            <div className="mb-6 flex gap-2">
              <button
                type="button"
                onClick={() => setShowTemplateDialog(true)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("meeting_minutes.templates.use_template")}
              </button>
              <button
                type="button"
                onClick={() => setShowCopyDialog(true)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("meeting_minutes.templates.copy_from")}
              </button>
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("meeting_minutes.basic_info")}
              </h3>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("meeting_minutes.title")} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("meeting_minutes.meeting_type")} *
                  </label>
                  <select
                    required
                    value={formData.meeting_type}
                    onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("meeting_minutes.select_type")}</option>
                    <option value="employee_interview">{t("meeting_minutes.type.employee_interview")}</option>
                    <option value="project_meeting">{t("meeting_minutes.type.project_meeting")}</option>
                    <option value="other">{t("meeting_minutes.type.other")}</option>
                  </select>
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("meeting_minutes.description")}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Employee selection for employee_interview */}
                {formData.meeting_type === "employee_interview" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("meeting_minutes.select_employee")} *
                    </label>
                    <PMCustomSelector
                      open={false}
                      title={t("meeting_minutes.select_employee")}
                      multiple={false}
                      field="displayTitle"
                      field2="email"
                      field3="mobile"
                      fetchFunction={async () => {
                        const response = await fetchUsers({
                          filters: [{ name: "EMPLOYEE", value: true }],
                          per_page: 100,
                        });
                        return response?.data?.data || response?.data || response || [];
                      }}
                      selectedItems={formData.employee_id ? [formData.employee_id] : []}
                      onChange={(e) => {
                        const selectedId = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
                        setFormData({ ...formData, employee_id: selectedId || null });
                      }}
                      values={{ employee_id: formData.employee_id }}
                      setFieldValue={(name, value) => {
                        setFormData({ ...formData, [name]: value });
                      }}
                    />
                  </div>
                )}

                {/* Project selection for project_meeting */}
                {formData.meeting_type === "project_meeting" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("meeting_minutes.select_project")}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowProjectSelector(true)}
                      className="w-full px-3 py-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {selectedProjectObject ? (
                        <span className="text-sm text-gray-900">
                          {selectedProjectObject.displayTitle || selectedProjectObject.name || `Project #${formData.project_id}`}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">
                          {t("meeting_minutes.select_project")}
                        </span>
                      )}
                    </button>
                  </div>
                )}

                {/* Participants selection for other meetings */}
                {formData.meeting_type === "other" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("meeting_minutes.select_participants")}
                      </label>
                      <PMCustomSelector
                        open={false}
                        title={t("meeting_minutes.select_participants")}
                        multiple={true}
                        field="displayTitle"
                        field2="email"
                        field3="mobile"
                        fetchFunction={async () => {
                          const response = await fetchUsers({
                            filters: [{ name: "EMPLOYEE", value: true }],
                            per_page: 100,
                          });
                          return response?.data?.data || response?.data || response || [];
                        }}
                        selectedItems={formData.participants || []}
                        onChange={(e) => {
                          const selectedIds = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                          setFormData({ ...formData, participants: selectedIds.filter(id => id) });
                        }}
                        values={{ participants: formData.participants }}
                        setFieldValue={(name, value) => {
                          setFormData({ ...formData, [name]: value });
                        }}
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {t("meeting_minutes.employee.add_manual")}
                        </label>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData({
                              ...formData,
                              manual_participants_list: [
                                ...formData.manual_participants_list,
                                { name: "", email: "", mobile: "" }
                              ]
                            });
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          <PlusIcon className="h-4 w-4 inline mr-1" />
                          {t("meeting_minutes.employee.add_manual")}
                        </button>
                      </div>
                      {formData.manual_participants_list.map((participant, index) => (
                        <div key={index} className="mb-3 p-3 border border-gray-300 rounded-md">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700">
                                {t("meeting_minutes.employee.manual_name")}
                              </label>
                              <input
                                type="text"
                                value={participant.name}
                                onChange={(e) => {
                                  const newList = [...formData.manual_participants_list];
                                  newList[index].name = e.target.value;
                                  setFormData({ ...formData, manual_participants_list: newList });
                                }}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700">
                                {t("meeting_minutes.employee.manual_email")}
                              </label>
                              <input
                                type="email"
                                value={participant.email}
                                onChange={(e) => {
                                  const newList = [...formData.manual_participants_list];
                                  newList[index].email = e.target.value;
                                  setFormData({ ...formData, manual_participants_list: newList });
                                }}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700">
                                {t("meeting_minutes.employee.manual_mobile")}
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="tel"
                                  value={participant.mobile}
                                  onChange={(e) => {
                                    const newList = [...formData.manual_participants_list];
                                    newList[index].mobile = e.target.value;
                                    setFormData({ ...formData, manual_participants_list: newList });
                                  }}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const newList = formData.manual_participants_list.filter((_, i) => i !== index);
                                    setFormData({ ...formData, manual_participants_list: newList });
                                  }}
                                  className="mt-1 text-red-600 hover:text-red-800"
                                >
                                  <XMarkIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Note: Meetings are now created separately via the meetings page */}
            {isEdit && (
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-600">
                  {t("meeting_minutes.meetings_created_separately")}
                </p>
              </div>
            )}
            
            {/* Old meetings structure removed - meetings are now created separately */}
            {false && meetings.map((meeting, meetingIndex) => (
              <div key={meetingIndex} className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t("meeting_minutes.meeting_number")} {meeting.meeting_number}
                  </h3>
                  {meetings.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newMeetings = meetings.filter((_, i) => i !== meetingIndex);
                        setMeetings(newMeetings);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("meeting_minutes.meeting_date")}
                  </label>
                  <input
                    type="date"
                    value={meeting.meeting_date}
                    onChange={(e) => {
                      const newMeetings = [...meetings];
                      newMeetings[meetingIndex].meeting_date = e.target.value;
                      setMeetings(newMeetings);
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Groups */}
                {meeting.groups.map((group, groupIndex) => (
                  <div key={groupIndex} className="mb-6 border-l-4 border-indigo-500 pl-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-md font-medium text-gray-800">
                        {t("meeting_minutes.group.title")} {groupIndex + 1}
                      </h4>
                      {meeting.groups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeGroup(meetingIndex, groupIndex)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("meeting_minutes.group.title")} *
                        </label>
                        <input
                          type="text"
                          required
                          value={group.title}
                          onChange={(e) => updateGroup(meetingIndex, groupIndex, "title", e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("meeting_minutes.group.description")}
                        </label>
                        <textarea
                          value={group.description}
                          onChange={(e) => updateGroup(meetingIndex, groupIndex, "description", e.target.value)}
                          rows={2}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Entries */}
                      <div className="ml-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            {t("meeting_minutes.group.entries")}
                          </label>
                          <button
                            type="button"
                            onClick={() => addEntry(meetingIndex, groupIndex)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <PlusIcon className="h-4 w-4 mr-1" />
                            {t("meeting_minutes.group.add_entry")}
                          </button>
                        </div>

                        {group.entries.map((entry, entryIndex) => (
                          <div key={entryIndex} className="bg-gray-50 p-4 rounded border">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                {t("meeting_minutes.entry.description")} {entryIndex + 1}
                              </span>
                              {group.entries.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeEntry(meetingIndex, groupIndex, entryIndex)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  {t("meeting_minutes.entry.description")} *
                                </label>
                                <textarea
                                  required
                                  value={entry.description}
                                  onChange={(e) => updateEntry(meetingIndex, groupIndex, entryIndex, "description", e.target.value)}
                                  rows={2}
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("meeting_minutes.entry.deadline")}
                                  </label>
                                  <input
                                    type="date"
                                    value={entry.deadline}
                                    onChange={(e) => updateEntry(meetingIndex, groupIndex, entryIndex, "deadline", e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    {t("meeting_minutes.entry.responsible")}
                                  </label>
                                  <PMCustomSelector
                                    open={false}
                                    title={t("meeting_minutes.entry.select_responsible")}
                                    multiple={false}
                                    field="displayTitle"
                                    field2="email"
                                    field3="mobile"
                                    fetchFunction={async () => {
                                      const response = await fetchUsers({
                                        filters: [{ name: "EMPLOYEE", value: true }],
                                        per_page: 100,
                                      });
                                      return response?.data?.data || response?.data || response || [];
                                    }}
                                    selectedItems={entry.responsible_user_id ? [entry.responsible_user_id] : []}
                                    onChange={(e) => {
                                      const selectedId = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
                                      updateEntry(meetingIndex, groupIndex, entryIndex, "responsible_user_id", selectedId || null);
                                    }}
                                    values={{ responsible_user_id: entry.responsible_user_id }}
                                    setFieldValue={(name, value) => {
                                      updateEntry(meetingIndex, groupIndex, entryIndex, name, value);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addGroup(meetingIndex)}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {t("meeting_minutes.add_group")}
                </button>
              </div>
            ))}


            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate("/dashboard/meeting-minutes")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? t("Saving...") : (isEdit ? t("Update") : t("Create"))}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Template Selection Dialog */}
      <PMCustomSelector
        open={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        onSelect={(template) => handleUseTemplate(template)}
        title={t("meeting_minutes.templates.select_template")}
        multiple={false}
        field="title"
        field2="meeting_type"
        dataSource={templates}
        customRender={(template) => (
          <div className="p-4 border-b">
            <div className="font-medium">{template.title}</div>
            <div className="text-sm text-gray-500">{template.meeting_type}</div>
          </div>
        )}
      />

      {/* Copy From Previous Meeting Dialog */}
      <PMCustomSelector
        open={showCopyDialog}
        onClose={() => setShowCopyDialog(false)}
        onSelect={(meeting) => handleCopyFrom(meeting)}
        title={t("meeting_minutes.templates.copy_from")}
        multiple={false}
        field="title"
        field2="meeting_type"
        dataSource={previousMeetings}
        customRender={(meeting) => (
          <div className="p-4 border-b">
            <div className="font-medium">{meeting.title}</div>
            <div className="text-sm text-gray-500">
              {meeting.meeting_type} • {meeting.meeting_date}
            </div>
          </div>
        )}
      />

      {/* Project Selector Dialog */}
      {showProjectSelector && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowProjectSelector(false)} 
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl h-[80vh] flex flex-col">
              <ModernProjectSelector
                open={showProjectSelector}
                selectedProjects={selectedProjectObject ? [selectedProjectObject] : []}
                onChange={(projects) => {
                  if (projects.length > 0) {
                    const project = projects[0];
                    setSelectedProjectObject(project);
                    setFormData({ ...formData, project_id: project.id });
                    setSelectedProject(project.id);
                  } else {
                    setSelectedProjectObject(null);
                    setFormData({ ...formData, project_id: null });
                    setSelectedProject(null);
                  }
                }}
                onClose={() => setShowProjectSelector(false)}
                multiple={false}
                showHeader={true}
                showSuggestedProjects={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
